import React, { useMemo } from 'react';
import { useGetStudentAttendanceHistoryQuery } from '../../store/api/localApi';
import { AttendanceEventType } from '../../types/attendance-v2';
import { addDays, dublinDateString, formatCivilDate } from '../../utils/date';

/** Trailing four weeks, today inclusive — the window an admin scans for a
 *  child who has quietly stopped coming. */
const WINDOW_DAYS = 28;

interface Props {
  studentId: string;
}

/**
 * A compact attendance summary on the child's profile.
 *
 * Admin-only, and the caller must not render it for anyone else: the
 * attendance-history endpoint 403s STAFF and PARENT alike — a parent gets 403
 * even on their own child — so mounting this for a non-admin would fire a
 * guaranteed-failing request.
 */
export const StudentAttendanceSection: React.FC<Props> = ({ studentId }) => {
  const to = dublinDateString();
  const from = addDays(to, -(WINDOW_DAYS - 1));

  const { data, isLoading, isError } = useGetStudentAttendanceHistoryQuery({
    studentId,
    from,
    to,
  });

  // Arrivals only: a CHECK_OUT/ROLLED_OUT marks the end of a visit that its
  // matching arrival already counted, so counting every event would double
  // each day the child was here.
  const arrivals = useMemo(
    () =>
      (data?.events ?? []).filter(
        (event) =>
          event.type === AttendanceEventType.CHECK_IN ||
          event.type === AttendanceEventType.ROLLED_IN
      ),
    [data]
  );

  // Events arrive ascending by `at`, so the last arrival is the most recent.
  const lastAttendedDate = arrivals.length > 0 ? arrivals[arrivals.length - 1].date : undefined;

  return (
    <section data-testid="student-attendance-section" className="mt-8">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">Attendance</h3>

      {isLoading && <p className="text-sm text-gray-500">Loading attendance…</p>}

      {isError && <p className="text-sm text-red-500">Could not load attendance.</p>}

      {!isLoading && !isError && (
        <div className="space-y-1">
          <p className="text-sm text-gray-700" data-testid="student-attendance-last">
            {lastAttendedDate
              ? `Last attended: ${formatCivilDate(lastAttendedDate)}`
              : `No attendance recorded in the last ${WINDOW_DAYS} days`}
          </p>
          <p className="text-xs text-gray-500" data-testid="student-attendance-count">
            {arrivals.length} check-{arrivals.length === 1 ? 'in' : 'ins'} in the last {WINDOW_DAYS}{' '}
            days
          </p>
        </div>
      )}
    </section>
  );
};

export default StudentAttendanceSection;
